import { useRef } from 'react';
import type { ChangeEvent, InputHTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';
import {
  Container,
  EndAdornment,
  Input,
  InputContainer,
} from './InputText.styles';

const Label = styled.label<{ $hidden?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 19.6px;
  color: #131313;
  font-family: 'Noto Sans', sans-serif;
  font-size: 14px;
  line-height: 1.4;
  white-space: nowrap;

  && {
    font-weight: 500;
  }

  ${({ $hidden }) =>
    $hidden &&
    `
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
    min-height: 0;
  `}
`;

const LabelText = styled.span`
  font-weight: 500;
`;

const RequiredMark = styled.span`
  color: #d83143;
  font-weight: 500;
`;

type InputTextProps = {
  id?: string;
  label: ReactNode;
  value: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  padding?: string;
  borderColor?: string;
  width?: string;
  inputHeight?: string;
  inputWidth?: string;
  disabled?: boolean;
  readOnly?: boolean;
  readOnlyMuted?: boolean;
  required?: boolean;
  mutedWhenInactive?: boolean;
  endAdornment?: ReactNode;
  hideLabel?: boolean;
} & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  | 'value'
  | 'onChange'
  | 'readOnly'
  | 'disabled'
  | 'type'
  | 'id'
  | 'required'
>;

let inputTextIdCounter = 0;

function useGeneratedInputId() {
  const idRef = useRef<string>();
  if (!idRef.current) {
    inputTextIdCounter += 1;
    idRef.current = `input-text-${inputTextIdCounter}`;
  }
  return idRef.current;
}

const InputText = ({
  label,
  value,
  onChange,
  padding,
  borderColor,
  width,
  inputHeight,
  inputWidth,
  disabled,
  readOnly,
  readOnlyMuted = true,
  required = false,
  mutedWhenInactive = true,
  endAdornment,
  hideLabel = false,
  id,
  maxLength,
  minLength,
  ...config
}: InputTextProps) => {
  const generatedId = useGeneratedInputId();
  const finalId = id ?? generatedId;
  const normalizedMaxLength =
    maxLength === undefined ? undefined : Number(maxLength);
  const normalizedMinLength =
    minLength === undefined ? undefined : Number(minLength);
  const isReadOnly = Boolean(readOnly && !disabled);
  const isInactive = Boolean(disabled || (readOnly && readOnlyMuted));
  const isCompact = inputHeight === '40px';
  const containerPadding = padding ?? (isCompact ? '0 12px' : undefined);

  return (
    <Container width={width}>
      <Label htmlFor={finalId} $hidden={hideLabel}>
        <LabelText>{label}</LabelText>
        {required ? <RequiredMark aria-hidden="true">*</RequiredMark> : null}
      </Label>
      <InputContainer
        padding={containerPadding}
        borderColor={borderColor}
        inputHeight={inputHeight}
        inputWidth={inputWidth}
        $isReadOnly={isReadOnly}
        $readOnlyMuted={readOnlyMuted}
        $isInactive={isInactive}
        $mutedWhenInactive={mutedWhenInactive}
        $isCompact={isCompact}
        $hasEndAdornment={Boolean(endAdornment)}
      >
        <Input
          id={finalId}
          type="text"
          {...config}
          value={value}
          onChange={onChange}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          maxLength={normalizedMaxLength}
          minLength={normalizedMinLength}
          $isReadOnly={isReadOnly && readOnlyMuted}
          $isInactive={isInactive}
          $isCompact={isCompact}
        />
        {endAdornment ? (
          <EndAdornment data-testid="input-end-adornment">
            {endAdornment}
          </EndAdornment>
        ) : null}
      </InputContainer>
    </Container>
  );
};

export default InputText;
